import { useEffect, useState, useRef } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import "./Users.css";
import Layout from "../../components/Layout";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import Avatar from "@mui/material/Avatar";
import PageHeader from "../../components/PageHeader/PageHeader";
import DataTable from "../../components/DataTable/DataTable";
import TablePagination from "../../components/DataTable/TablePagination";
import RowActions from "../../components/DataTable/RowActions";
import useCachedFetch from "../../hooks/useCachedFetch";
import useBreakpoint from "../../hooks/useBreakpoint";

const PAGE_SIZE = 15;

const statusConfig = {
  active: { label: "Active", className: "active" },
  blocked: { label: "Blocked", className: "status-blocked" },
  archived: { label: "Archived", className: "status-archived" },
  incomplete: { label: "Incomplete", className: "status-incomplete" },
  pending_approval: { label: "Pending", className: "status-pending" },
  rejected: { label: "Rejected", className: "status-rejected" },
};

const statusFilters = ["active", "pending_approval", "blocked", "archived"];

const accountTypeClass = (type) => {
  switch (type) {
    case "business":
      return "badge-business";
    case "freelancer":
      return "badge-freelancer";
    default:
      return "badge-user";
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  let date;
  if (timestamp._seconds) date = new Date(timestamp._seconds * 1000);
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" });
  return `${day} ${month}, ${date.getFullYear()}`;
};

const Users = () => {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  const [search, setSearch] = useState("");
  const [loadedAvatars, setLoadedAvatars] = useState({});

  const [filters, setFilters] = useState({
    account_status: "active",
    account_type: "all",
    sort: "latest",
    search: "",
  });

  const {
    data: users,
    total,
    loading,
    hasMore,
    page,
    fetch,
    refetch,
    invalidate,
    nextPage,
    prevPage,
  } = useCachedFetch({
    cacheKey: "users",
    endpoint: "/users",
    params: filters,
    limit: PAGE_SIZE,
  });

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  // Fetch on filter change
  useEffect(() => {
    fetchRef.current(0);
  }, [filters]);

  // Debounce search → update filters.search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) =>
        prev.search === search ? prev : { ...prev, search },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Avatar shimmer preload
  useEffect(() => {
    const newLoaded = {};
    users.forEach((user) => {
      if (!user.profile_picture) {
        newLoaded[user.id] = true;
      } else {
        const img = new Image();
        img.src = user.profile_picture;
        if (img.complete) newLoaded[user.id] = true;
      }
    });
    setLoadedAvatars(newLoaded);
  }, [users]);

  /* ── CRUD ── */
  const runAction = async (request, successMessage, errorMessage) => {
    try {
      await request();
      toast.success(successMessage);
      invalidate();
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || errorMessage);
    }
  };

  const deleteUser = (id) =>
    runAction(
      () => api.delete(`/users/${id}`),
      "Account deleted successfully!",
      "Failed to delete user account!",
    );
  const archiveUser = (id) =>
    runAction(
      () => api.patch(`/users/${id}/archive`),
      "User Archived!",
      "Failed to archive user!",
    );
  const blockUser = (id) =>
    runAction(
      () => api.patch(`/users/${id}/block`),
      "User Blocked!",
      "Failed to block user!",
    );
  const recoverUser = (id) =>
    runAction(
      () => api.patch(`/users/${id}/unarchive`),
      "User account recovered!",
      "Failed to recover user account!",
    );
  const unblockUser = (id) =>
    runAction(
      () => api.patch(`/users/${id}/unblock`),
      "User account unblocked!",
      "Failed to unblock user account!",
    );
  const approveUser = (id) =>
    runAction(
      () => api.patch(`/users/${id}/approve`),
      "Account approved!",
      "Failed to approve account!",
    );
  const rejectUser = (id, reason) =>
    runAction(
      () => api.patch(`/users/${id}/reject`, { reason }),
      "Account rejected.",
      "Failed to reject account!",
    );

  /* ── CONFIRM MODAL ── */
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "delete",
  });

  const closeConfirm = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "delete",
    });
  };

  const confirmAction = ({ title, message, type, run }) => {
    setConfirmModal({
      open: true,
      title,
      message,
      type,
      onConfirm: () => {
        run();
        closeConfirm();
      },
    });
  };

  /* ── ROW ACTIONS ── */
  const buildActions = (user) => {
    const name = <strong>{user.public_username}</strong>;
    const isPending = user.account_status === "pending_approval";
    const isBlocked = user.account_status === "blocked";
    const isArchived = user.account_status === "archived";

    const inline = [
      {
        key: "view",
        label: "View",
        icon: "visibility",
        color: "neutral",
        onClick: () => toast.info("User details coming soon"),
      },
      ...(isPending
        ? [
            {
              key: "approve",
              label: "Approve",
              icon: "check_circle",
              color: "primary",
              onClick: () =>
                confirmAction({
                  title: "Approve Account",
                  type: "approve",
                  message: (
                    <span>
                      Are you sure you want to approve {name}?<br />
                      They will become visible to customers and able to receive
                      bookings.
                    </span>
                  ),
                  run: () => approveUser(user.id),
                }),
            },
            {
              key: "reject",
              label: "Reject",
              icon: "cancel",
              color: "error",
              onClick: () =>
                confirmAction({
                  title: "Reject Account",
                  type: "reject",
                  message: (
                    <span>
                      Are you sure you want to reject {name}'s application?
                      <br />
                      They will need to be re-approved separately to access the
                      platform.
                    </span>
                  ),
                  run: () => rejectUser(user.id),
                }),
            },
          ]
        : [
            isBlocked
              ? {
                  key: "unblock",
                  label: "Unblock",
                  icon: "lock_open",
                  color: "primary",
                  onClick: () =>
                    confirmAction({
                      title: "Unblock User",
                      type: "unblock",
                      message: (
                        <span>
                          Are you sure you want to unblock {name}?<br />
                          They will be able to access their account & resume
                          activity.
                        </span>
                      ),
                      run: () => unblockUser(user.id),
                    }),
                }
              : {
                  key: "block",
                  label: "Block",
                  icon: "block",
                  color: "neutral",
                  disabled: isArchived,
                  onClick: () =>
                    confirmAction({
                      title: "Block User",
                      type: "block",
                      message: (
                        <span>
                          Are you sure you want to block {name}?<br />
                          They won't be able to access their account. You can
                          un-block this account later.
                        </span>
                      ),
                      run: () => blockUser(user.id),
                    }),
                },
            isArchived
              ? {
                  key: "recover",
                  label: "Recover",
                  icon: "restart_alt",
                  color: "primary",
                  onClick: () =>
                    confirmAction({
                      title: "Recover User",
                      type: "recover",
                      message: (
                        <span>
                          Are you sure you want to recover {name}?<br />
                          They will be able to access their account & resume
                          activity.
                        </span>
                      ),
                      run: () => recoverUser(user.id),
                    }),
                }
              : {
                  key: "archive",
                  label: "Archive",
                  icon: "delete",
                  color: "error",
                  onClick: () =>
                    confirmAction({
                      title: "Archive User",
                      type: "delete",
                      message: (
                        <span>
                          Are you sure you want to temporarily delete {name}?
                          <br />
                          They won't be able to access their account. You can
                          recover this account later.
                        </span>
                      ),
                      run: () => archiveUser(user.id),
                    }),
                },
          ]),
    ];

    const menu = [
      {
        key: "copy",
        label: "Copy Email",
        icon: "content_copy",
        onClick: () => {
          navigator.clipboard.writeText(user.email_address);
          toast.success("Email copied!");
        },
      },
      {
        key: "reset",
        label: "Reset Password",
        icon: "lock_reset",
        onClick: () => toast.info("Reset password coming soon"),
      },
      {
        key: "delete-permanent",
        label: "Delete Permanently",
        icon: "delete",
        danger: true,
        hidden: !isArchived,
        onClick: () =>
          confirmAction({
            title: "Delete User",
            type: "permanent",
            message: (
              <span>
                Are you sure you want to permanently delete {name}?<br />
                This action can't be undone!
              </span>
            ),
            run: () => deleteUser(user.id),
          }),
      },
    ];

    return { inline, menu };
  };

  /* ── TABLE COLUMNS ── */
  const columns = [
    {
      key: "user",
      header: "Username / Email",
      width: "minmax(220px, 1fr)",
      skeleton: (
        <div className="user-cell">
          <div className="skeleton skeleton-avatar" />
          <div className="skeleton-text-group">
            <div className="skeleton skeleton-text" />
            <div className="skeleton skeleton-text short" />
          </div>
        </div>
      ),
      render: (user) => (
        <div className="user-cell">
          <div className="avatar-wrapper">
            {!loadedAvatars[user.id] && user.profile_picture && (
              <div className="avatar-shimmer" />
            )}
            <Avatar
              src={user.profile_picture}
              onLoad={() =>
                setLoadedAvatars((prev) => ({ ...prev, [user.id]: true }))
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#E5E9E5",
                color: "#006663",
                opacity:
                  loadedAvatars[user.id] || !user.profile_picture ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            >
              {user.public_username?.charAt(0)?.toUpperCase() || "A"}
            </Avatar>
          </div>
          <div className="user-cell__text">
            <div className="name">{user.public_username || "No Name"}</div>
            <div className="email">{user.email_address || "No Email"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: "minmax(96px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-badge" />,
      render: (user) => (
        <span className={`badge ${accountTypeClass(user.account_type)}`}>
          {user.account_type || "Unknown"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "minmax(96px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-badge" />,
      render: (user) => {
        const status = statusConfig[user.account_status] || {
          label: user.account_status || "Unknown",
          className: "status-unknown",
        };
        return (
          <span className={`status ${status.className}`}>{status.label}</span>
        );
      },
    },
    {
      key: "joined",
      header: "Joined",
      width: "minmax(104px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-text short" />,
      render: (user) => (
        <span style={{ fontSize: "14px" }}>{formatDate(user.created_at)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: isMobile ? "40px" : "minmax(290px, auto)",
      align: "end",
      sticky: isMobile,
      skeleton: isMobile ? (
        <div className="skeleton skeleton-btn" style={{ width: 28 }} />
      ) : (
        <div className="skeleton-actions">
          <div className="skeleton skeleton-btn" />
          <div className="skeleton skeleton-btn" />
          <div className="skeleton skeleton-btn" />
        </div>
      ),
      render: (user) => {
        const { inline, menu } = buildActions(user);
        return <RowActions inline={inline} menu={menu} collapsed={isMobile} />;
      },
    },
  ];

  /* ── UI ── */
  return (
    <Layout>
      <div className="users-page">
        <PageHeader
          title="Users Management"
          description="Manage all of the platform users under one place."
          filters={
            <div className="status-chips">
              {statusFilters.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`chip ${filters.account_status === item ? "active" : ""}`}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, account_status: item }))
                  }
                >
                  {statusConfig[item]?.label?.toUpperCase() ||
                    item.toUpperCase()}
                </button>
              ))}
            </div>
          }
          actions={
            <div className="controls">
              <div className="search-wrapper">
                <span className="material-symbols-outlined search-icon">
                  search
                </span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <span
                    className="material-symbols-outlined clear-icon"
                    onClick={() => setSearch("")}
                  >
                    close
                  </span>
                )}
              </div>

              <div className="select-wrapper">
                <select
                  aria-label="Sort users"
                  value={filters.sort}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, sort: e.target.value }))
                  }
                >
                  <option value="latest">Default Order</option>
                  <option value="oldest">Oldest First</option>
                  <option value="a-z">Name (A → Z)</option>
                  <option value="z-a">Name (Z → A)</option>
                </select>
                <span className="material-symbols-outlined select-arrow">
                  expand_more
                </span>
              </div>

              <div className="select-wrapper">
                <select
                  aria-label="Filter by account type"
                  value={filters.account_type}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      account_type: e.target.value,
                    }))
                  }
                >
                  <option value="all">All Users</option>
                  <option value="customer">Customers</option>
                  <option value="freelancer">Freelancers</option>
                  <option value="business">Businesses</option>
                </select>
                <span className="material-symbols-outlined select-arrow">
                  expand_more
                </span>
              </div>
            </div>
          }
        />

        <DataTable
          columns={columns}
          rows={users}
          loading={loading}
          skeletonRows={PAGE_SIZE}
          emptyMessage="No users found"
          minWidth={isMobile ? 640 : 900}
          footer={
            <TablePagination
              page={page}
              limit={PAGE_SIZE}
              total={total}
              count={users.length}
              hasMore={hasMore}
              loading={loading}
              onPrev={prevPage}
              onNext={nextPage}
              noun="users"
            />
          }
        />
      </div>

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        type={confirmModal.type}
      />
    </Layout>
  );
};

export default Users;
